import { NotifyErrorWrapper, NotifySuccess } from '@/helpers/core/notifications'
import { createSite, deleteSite, getAllSites, getSiteByID, getSites, updateSite } from '@/services/core.service'
import type { ILiteSiteFilters, ISiteFilters } from '@/types/core/filters'
import type { ICreateSiteForm, IUpdateSiteForm } from '@/types/core/forms'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed } from 'vue'
import type { Reactive, Ref } from 'vue'

export const useSites = () => {
  const client = useQueryClient()

  const siteDetailsQuery = (siteId: Ref<string | undefined>) =>
    useQuery({
      queryKey: ['siteDetails', siteId],
      queryFn: () => getSiteByID(siteId.value!),
      enabled: computed(() => !!siteId.value),
    })

  const getSitesQuery = (filters: Reactive<ISiteFilters>) =>
    useQuery({
      queryKey: ['sites', filters],
      queryFn: () => getSites(filters),
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    })

  const getAllSitesQuery = (filters?: Ref<ILiteSiteFilters>) =>
    useQuery({
      queryKey: ['sites', 'lite', filters],
      queryFn: () => getAllSites(filters?.value),
      refetchOnWindowFocus: true,
      refetchOnMount: true,
    })

  const createSiteMutation = useMutation({
    mutationFn: (form: ICreateSiteForm) => createSite(form),
    onError: NotifyErrorWrapper('Failed to create site'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['sites'] })
      NotifySuccess(`Site created successfully`)
    },
  })

  const updateSiteMutation = useMutation({
    mutationFn: (form: IUpdateSiteForm) => updateSite(form),
    onError: NotifyErrorWrapper('Failed to update site'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['sites'] })
      NotifySuccess(`Site updated successfully`)
    },
  })

  const deleteSiteMutation = useMutation({
    mutationFn: (id: number) => deleteSite(id),
    onError: NotifyErrorWrapper('Failed to delete site'),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['sites'] })
      NotifySuccess(`Site deleted successfully`)
    },
  })

  return {
    siteDetailsQuery,
    getSitesQuery,
    getAllSitesQuery,
    createSiteMutation,
    updateSiteMutation,
    deleteSiteMutation,
  }
}
